import axios from "axios";
import { logger } from "@/utils/logger";
import { getApiBaseUrl } from "@/constants/api";

const API_URL = getApiBaseUrl();

logger.info(`Khởi tạo API Client với Base URL: ${API_URL}`);

// Tạo axios instance
export const apiClient = axios.create({
  baseURL: API_URL,
  timeout: 30000,
  headers: {
    "Content-Type": "application/json",
  },
});

// Request interceptor: đặt lại baseURL mỗi request để server dùng API_URL runtime nếu có
apiClient.interceptors.request.use(
  (config) => {
    const baseURL = getApiBaseUrl();
    config.baseURL = baseURL;

    // Auth: token và X-User-Id từ cookie (tương tự admin)
    if (typeof window !== "undefined") {
      const token = document.cookie
        .split("; ")
        .find((row) => row.startsWith("auth-token="))
        ?.split("=")[1];

      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }

      const userId = document.cookie
        .split("; ")
        .find((row) => row.startsWith("app_user_id="))
        ?.split("=")[1];
      if (userId) {
        config.headers["X-User-Id"] = userId;
      }
    }

    logger.debug(`[API Request] ${config.method?.toUpperCase()} ${config.url}`, {
      baseURL,
      fullURL: `${baseURL}${config.url}`,
      headers: config.headers,
    });
    return config;
  },
  (error) => {
    logger.error("[API Request Error]", error);
    return Promise.reject(error);
  }
);

// Response interceptor
apiClient.interceptors.response.use(
  (response) => {
    logger.success(`[API Response] ${response.status} ${response.config.url}`);
    return response;
  },
  async (error) => {
    const originalRequest = error.config;
    const status = error.response?.status;
    const isGet = originalRequest?.method?.toLowerCase() === "get";
    const is404 = status === 404;
    const isAborted = error.code === "ECONNABORTED" || error.code === "ERR_CANCELED";
    const isConnectionRefused =
      error.code === "ECONNREFUSED" ||
      error.code === "ERR_NETWORK" ||
      (!status && !error.response);

    if (isAborted) {
      logger.warn(`[API Request Aborted] ${originalRequest?.url}`, {
        message: error.message,
        code: error.code,
      });
    } else if (isGet && is404) {
      logger.debug(`[API Response] 404 ${originalRequest?.url}`, {
        message: error.message,
        data: error.response?.data,
      });
    } else if (isConnectionRefused) {
      logger.warn(`[API Offline] ${originalRequest?.url}`, {
        message: error.message,
        code: error.code,
        baseURL: originalRequest?.baseURL,
        fullURL: `${originalRequest?.baseURL}${originalRequest?.url}`,
      });
    } else {
      logger.error(`[API Response Error] ${status || "No Status"} ${originalRequest?.url}`, {
        message: error.message,
        data: error.response?.data,
        code: error.code,
        baseURL: originalRequest?.baseURL,
        fullURL: `${originalRequest?.baseURL}${originalRequest?.url}`,
      });
    }

    if (status === 403) {
      logger.warn("Access denied", {
        url: originalRequest?.url,
        status,
      });
    }

    return Promise.reject(error);
  }
);

export default apiClient;
